import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, ShoppingCart, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/context/CartContext';
import { useFavorites } from '@/context/FavoritesContext';
import { useSite } from '@/context/SiteContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b3658f45-91a7-444d-afc2-1a3b5556caed/artifacts/3z2poj6e_logo.png';

export const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { getCartCount, openCart } = useCart();
  const { getFavoritesCount } = useFavorites();
  const { siteData } = useSite();

  const categories = siteData?.categories || [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/loja?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const cartCount = getCartCount();
  const favoritesCount = getFavoritesCount();

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-pink-50 via-purple-50 to-pink-50 backdrop-blur-md border-b border-primary/10 shadow-sm">
      {/* Main Header */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Logo - styled to blend with white background */}
          <Link to="/" className="flex-shrink-0 bg-transparent">
            <img 
              src={LOGO_URL} 
              alt="Festa Encantada"
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </Link>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-4">
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Pesquisar produtos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full border-border bg-muted/50 focus:bg-card"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Search Toggle */}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-foreground">
                  <Search className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="top" className="h-auto">
                <form onSubmit={handleSearch} className="pt-4">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Pesquisar produtos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-full"
                      autoFocus
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  </div>
                </form>
              </SheetContent>
            </Sheet>

            {/* Favorites */}
            <Link to="/favoritos">
              <Button variant="ghost" size="icon" className="relative text-foreground hover:text-primary">
                <Heart className="h-5 w-5" />
                {favoritesCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-primary text-primary-foreground text-xs">
                    {favoritesCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Cart */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative text-foreground hover:text-primary"
              onClick={openCart}
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-primary text-primary-foreground text-xs">
                  {cartCount}
                </Badge>
              )}
            </Button>

            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden text-foreground"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5 border-t border-primary/10">
        <div className="container mx-auto px-4">
          <ul className="flex items-center justify-center gap-1 py-2 overflow-x-auto">
            <li>
              <Link 
                to="/loja" 
                className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors rounded-lg hover:bg-primary/5"
              >
                Todos os Produtos
              </Link>
            </li>
            {categories.slice(0, 8).map((category) => (
              <li key={category.id}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors rounded-lg hover:bg-primary/5">
                      {category.name}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="min-w-[160px]">
                    <DropdownMenuItem asChild>
                      <Link to={`/loja?categoria=${category.id}`} className="w-full">
                        Ver todos
                      </Link>
                    </DropdownMenuItem>
                    {category.subcategories?.map((sub) => (
                      <DropdownMenuItem key={sub.id} asChild>
                        <Link to={`/loja?categoria=${category.id}&sub=${sub.id}`} className="w-full">
                          {sub.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <nav className="md:hidden bg-card border-t border-border animate-slide-up">
          <div className="container mx-auto px-4 py-4">
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/loja" 
                  className="block px-4 py-3 text-foreground font-medium hover:bg-muted rounded-lg"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Todos os Produtos
                </Link>
              </li>
              {categories.map((category) => (
                <li key={category.id}>
                  <Link 
                    to={`/loja?categoria=${category.id}`}
                    className="block px-4 py-3 text-foreground font-medium hover:bg-muted rounded-lg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      )}
    </header>
  );
};
